import React, { useEffect, useRef, useState } from "react";
import { StoryblokComponent } from "@storyblok/react";
import { useLocation, useNavigate } from "react-router-dom";
import Utils from "../../core/Utils/Utils";
import { SupportModel } from "./SupportModel";
import SupportUtils from "../SupportUtils";
import { SupportFaqModel } from "../SupportFaq/SupportFaqModel";
import SupportFaq from "../SupportFaq/SupportFaq";
import { ButtonModel } from "../../core/Models/ButtonModel";
import Button from "../../core/Button/Button";
import { LevelJourneyModel } from "../LevelJourney/LevelJourneyModel";
import YoutubePopup from "../../core/YoutubePopup/YoutubePopup";
import { themeService } from "../../core/NotificationStripNav/ThemeService";
import { Theme } from "../../core/Models/CustomSiteConfigModel";

import "./Support.css";

const Support: React.FC<{ blok: SupportModel }> = ({ blok }) => {
  const defaultFaqToDisplay = 6;
  const location = useLocation();
  const navigate = useNavigate();
  const supportFaqsDiv = useRef<HTMLDivElement>(null);

  const [theme, setTheme] = useState<Theme | null>(themeService.getTheme());
  const [showPopup, setShowPopup] = useState<string | undefined>(undefined);
  const [notificationHeight, setNotificationHeight] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [faqToDisplay, setFaqToDisplay] = useState<number | undefined>(
    defaultFaqToDisplay
  );
  const [popularFaqs, setPopularFaqs] = useState<SupportFaqModel[]>([]);
  const [moreFaqs, setMoreFaqs] = useState<SupportFaqModel[]>([]);
  const [tag, setTag] = useState("");

  const button: ButtonModel = {
    link: { cached_url: "support/contact-us", linktype: "story" },
    label: "CONTACT US",
    borderColor: "#FFFFFF",
    color: "#0981F0",

    textColor: "#FFFFFF",
    hoverColor: "#000000",
  };

  useEffect(() => {
    const unsubscribe = themeService.subscribe(setTheme);
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const notificationElement = document.querySelector(
      ".notification-strip-nav"
    );
    if (notificationElement instanceof HTMLElement) {
      setNotificationHeight(notificationElement.offsetHeight);
    }

    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const query = params.get("query") || "";
    setSearchTerm(query || "");
    setDebouncedSearchTerm(query || "");

    SupportUtils.getCachedOrStoryblokSearch(
      query,
      faqToDisplay,
      true,
      blok.category,
      tag,
      "articles/faq/"
    )
      .then((response: any) => {
        setPopularFaqs(response.data.story || response.data?.stories);
      })
      .catch((error) => {
        if (error.status === 404) {
          navigate("/404");
        } else if (error.status === 500) {
          navigate("/500");
        }
      });

    SupportUtils.getCachedOrStoryblokSearch(
      query,
      faqToDisplay,
      false,
      blok.category,
      tag,
      "articles/faq/"
    )
      .then((response: any) => {
        setMoreFaqs(response.data.story || response.data?.stories);
      })
      .catch((error) => {
        if (error.status === 404) {
          navigate("/404");
        } else if (error.status === 500) {
          navigate("/500");
        }
      });
  }, [
    debouncedSearchTerm,
    faqToDisplay,
    location,
    blok.category,
    tag,
    navigate,
  ]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    Utils.setNavidation([{ param: "query", value }]);
    setSearchTerm(value);
  };

  const handleLoadMoreClick = (event: React.MouseEvent) => {
    event.preventDefault();
    setFaqToDisplay(undefined);
  };

  const handleShowLessClick = (event: React.MouseEvent) => {
    event.preventDefault();
    setFaqToDisplay(defaultFaqToDisplay);
  };

  const handleBackToSupportClick = () => {
    navigate(`/support/get-help`);
  };

  const handleGettingStartedItemClick = (uuid: string | undefined) => {
    setShowPopup(uuid);
  };

  const handleSupportItemClick = (item: LevelJourneyModel) => {
    setTag(item.tag);
    if (supportFaqsDiv?.current) {
      const elementPosition =
        supportFaqsDiv.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top: elementPosition - notificationHeight });
    }
  };

  return (
    <div
      className="support-account"
      style={{ backgroundColor: theme?.panelColour, color: theme?.textColor }}
    >
      <div className="container">
        <div className="support-title">
          <span className="back-to-support" onClick={handleBackToSupportClick}>
            BACK TO AUDIOMOVERS SUPPORT
          </span>
          <span> | {blok.title}</span>
        </div>
        <div className="support-subtitle">{blok.description}</div>

        <div
          className={`support-account-items ${
            blok.items?.length === 3 ? "three-items" : "two-items"
          }`}
        >
          {blok.items &&
            blok.items.map((item) => (
              <React.Fragment key={`YoutubePopupContainer-${item._uid}`}>
                <StoryblokComponent
                  blok={item}
                  key={item._uid}
                  handleSupportItemClick={() => handleSupportItemClick(item)}
                  handleGettingStartedItemClick={() =>
                    handleGettingStartedItemClick(item._uid)
                  }
                />
                {showPopup && showPopup === item._uid && (
                  <YoutubePopup
                    videoId={item.gettingStartedVideoId}
                    onButtonClick={() => setShowPopup(undefined)}
                    key={`YoutubePopup-${item._uid}`}
                  ></YoutubePopup>
                )}
              </React.Fragment>
            ))}
        </div>

        <div className="support-faqs" ref={supportFaqsDiv}>
          <span>{blok.popularFaqsTitle}</span>{" "}
          {tag && (
            <div
              className="support-tag"
              style={{ backgroundColor: theme?.containerColor }}
            >
              <span className="support-tag-clear" onClick={() => setTag("")}>
                x{" "}
              </span>
              <span className="support-tag-name">{tag}</span>
            </div>
          )}
        </div>
        <div className="support-filter">
          <input
            placeholder="Filter FAQs based on keyword"
            type="text"
            className="input-field"
            value={searchTerm}
            onChange={handleInputChange}
          ></input>
        </div>
        <div className="support-faqs-container">
          {popularFaqs &&
            popularFaqs.map((item) => (
              <SupportFaq blok={item} key={item.uuid}></SupportFaq>
            ))}
        </div>

        <div className="support-need-help">Still need help?</div>
        <Button blok={button}></Button>

        <div className="support-faqs">
          <span>{blok.moreFaqsTitle}</span>
        </div>
        <div className="support-faqs-container">
          {moreFaqs &&
            moreFaqs.map((item) => (
              <SupportFaq blok={item} key={item.uuid}></SupportFaq>
            ))}
        </div>
        {faqToDisplay !== undefined && (
          <div className="support-load-more">
            <a
              href={blok._uid}
              onClick={handleLoadMoreClick}
              style={{ color: theme?.textColor }}
            >
              Load more FAQs
            </a>
          </div>
        )}
        {faqToDisplay === undefined && (
          <div className="support-load-more">
            <a
              href={blok._uid}
              onClick={handleShowLessClick}
              style={{ color: theme?.textColor }}
            >
              Show less FAQs
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default Support;
